import React, { useEffect } from 'react';
import * as S from './order-bump.organism.style';
import { CheckboxV2 } from '../../atoms/v2/checkbox/checkbox.atom';
import { OrderBump as OrderBumpType } from '../../../common/types/order-bump';
import { formatCurrency } from '../../../common/utils/format-currency';
import { Checkout } from '../../../common/types/checkout';

type Props = {
  onToggle: (checked: boolean, id: string) => void;
  isChecked: boolean;
  orderBump: OrderBumpType;
  checkout: Checkout;
};

export const OrderBump: React.FC<Props> = ({
  isChecked,
  onToggle,
  orderBump,
  checkout,
}) => {
  const [checked, setChecked] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const triggerConfetti = (el: HTMLElement) => {
    const rect = el.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const colors = [
      '#FFD700',
      '#FF6B6B',
      '#4ECDC4',
      '#45B7D1',
      '#FFA07A',
      '#98D8C8',
      '#F7DC6F',
      '#BB8FCE',
    ];
    const confettiCount = 40;

    for (let i = 0; i < confettiCount; i++) {
      const confetti = document.createElement('div');
      confetti.className = 'confetti';
      confetti.style.left = `${centerX}px`;
      confetti.style.top = `${centerY}px`;
      confetti.style.backgroundColor =
        colors[Math.floor(Math.random() * colors.length)];

      const angle = (Math.PI * 2 * i) / confettiCount;
      const velocity = 100 + Math.random() * 150;
      const tx = Math.cos(angle) * velocity;
      const ty = Math.sin(angle) * velocity;

      confetti.style.setProperty('--tx', `${tx}px`);
      confetti.style.setProperty('--ty', `${ty}px`);

      document.body.appendChild(confetti);
      setTimeout(() => confetti.remove(), 2000);
    }
  };
  const toggleCheck = () => {
    const next = !checked;
    setChecked(next);
    if (next && containerRef.current) {
      triggerConfetti(containerRef.current);
    }
    onToggle(next, orderBump.id);
  };
  function calculateDiscountPercentage(
    originalPrice: number,
    finalPrice: number,
  ): number {
    if (originalPrice <= 0) return 0;
    const discount = ((originalPrice - finalPrice) / originalPrice) * 100;
    return Math.max(0, Math.ceil(discount));
  }
  useEffect(() => {
    setChecked(isChecked);
  }, []);
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes confetti-burst {
        0% {
          transform: translate(0, 0) rotate(0deg) scale(1);
          opacity: 1;
        }
        100% {
          transform: translate(var(--tx), var(--ty)) rotate(720deg) scale(0);
          opacity: 0;
        }
      }

      .confetti {
        position: fixed;
        width: 10px;
        height: 10px;
        z-index: 9999;
        animation: confetti-burst 2s ease-out forwards;
        pointer-events: none;
        border-radius: 50%;
      }

      @keyframes fall {
        to {
          transform: translateY(100vh) rotate(360deg);
        }
      }

      .falling-confetti {
        position: fixed;
        top: -10px;
        width: 10px;
        height: 10px;
        z-index: 9999;
        animation: fall linear forwards;
        pointer-events: none;
        border-radius: 50%;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <S.OrderBumpContainer onClick={toggleCheck} ref={containerRef} isChecked={checked}>
      <S.OrderBumpHeader isChecked={checked}>
        <CheckboxV2 type="checkbox" checked={checked} onChange={toggleCheck} />
        <S.OrderBumpTitle isChecked={checked}>
          {orderBump?.precos?.[0]?.titulo}
        </S.OrderBumpTitle>
      </S.OrderBumpHeader>

      <S.OrderBumpContent isChecked={checked}>
        <S.ProductInfo>
          {orderBump?.precos?.[0]?.exibirImagem && (
            <S.BumpProductImage
              src={orderBump?.produtoOferta?.imagemProduto}
              alt="Imagem do produto"
            />
          )}
          <S.ProductDetailsWrapper>
            <S.ProductDetails>
              <S.ProductName isChecked={checked}>
                {orderBump?.precos?.[0]?.nomeExibido}
              </S.ProductName>
            </S.ProductDetails>

            <S.ProductDescription isChecked={checked}>
              {orderBump?.precos?.[0]?.descricao}
            </S.ProductDescription>
          </S.ProductDetailsWrapper>
        </S.ProductInfo>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
          }}
        >
          <S.PriceRow>
            <S.OriginalPrice isChecked={checked}>
              {formatCurrency(
                orderBump?.precos?.[0]?.precoBase,
                checkout?.moeda,
              )}
            </S.OriginalPrice>
            <S.FinalPrice isChecked={checked}>
              {formatCurrency(
                orderBump?.precos?.[0]?.valor,
                checkout?.moeda,
              )}
            </S.FinalPrice>
          </S.PriceRow>
          <S.DiscountBadge>
            {calculateDiscountPercentage(
              orderBump?.precos?.[0]?.precoBase,
              orderBump?.precos?.[0]?.valor,
            )}
            % OFF
          </S.DiscountBadge>
        </div>
      </S.OrderBumpContent>
    </S.OrderBumpContainer>
  );
};
